/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo42B from "@/assets/images/42b-logo.jpg";
import aboutAirportAerial from "@/assets/images/about-airport-aerial.jpeg";
import aboutRunwayView from "@/assets/images/about-runway-view.jpeg";

// Merchandise images
import merchandise42bCap from "@/assets/images/merchandise/42b-cap.jpg";
import merchandiseLogoCap from "@/assets/images/merchandise/logo-cap.jpg";
import merchandise42bMensT from "@/assets/images/merchandise/42b-mens-t.jpg";
import merchandiseLogoMensT from "@/assets/images/merchandise/logo-mens-t.jpg";
import merchandise42bWomensT from "@/assets/images/merchandise/42b-womens-t.jpg";
import merchandiseLogoWomensT from "@/assets/images/merchandise/logo-womens-t.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings
  "/images/42b-logo.jpg": logo42B,
  "/assets/images/42b-logo.jpg": logo42B,
  "//img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/42B-logo_cmyk_30aug21out.jpg/:/rs=h:100,cg:true,m/qt=q:95": logo42B,
  "/42b-logo.jpg": logo42B,

  // About section images
  "/images/about-airport-aerial.jpeg": aboutAirportAerial,
  "/assets/images/about-airport-aerial.jpeg": aboutAirportAerial,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/02C1C195-3783-4D85-B8D9-7B9F6B9727BD.jpeg/:/rs=w:600,h:300,cg:true,m/cr=w:600,h:300": aboutAirportAerial,
  
  "/images/about-runway-view.jpeg": aboutRunwayView,
  "/assets/images/about-runway-view.jpeg": aboutRunwayView,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/BC2B586A-6043-490F-A669-61A9F7379FC7.jpeg/:/rs=w:600,h:300,cg:true,m/cr=w:600,h:300": aboutRunwayView,

  // Merchandise images
  "/merchandise/42b-cap.jpg": merchandise42bCap,
  "/assets/images/merchandise/42b-cap.jpg": merchandise42bCap,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/ols/42B%20cap.jpg": merchandise42bCap,

  "/merchandise/logo-cap.jpg": merchandiseLogoCap,
  "/assets/images/merchandise/logo-cap.jpg": merchandiseLogoCap,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/ols/logo%20cap.jpg": merchandiseLogoCap,

  "/merchandise/42b-mens-t.jpg": merchandise42bMensT,
  "/assets/images/merchandise/42b-mens-t.jpg": merchandise42bMensT,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/ols/42B%20men's%20t%20front.jpg": merchandise42bMensT,

  "/merchandise/logo-mens-t.jpg": merchandiseLogoMensT,
  "/assets/images/merchandise/logo-mens-t.jpg": merchandiseLogoMensT,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/ols/logo%20men's%20t%20front.jpg": merchandiseLogoMensT,

  "/merchandise/42b-womens-t.jpg": merchandise42bWomensT,
  "/assets/images/merchandise/42b-womens-t.jpg": merchandise42bWomensT,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/ols/42B%20women's%20t%20front.jpg": merchandise42bWomensT,

  "/merchandise/logo-womens-t.jpg": merchandiseLogoWomensT,
  "/assets/images/merchandise/logo-womens-t.jpg": merchandiseLogoWomensT,
  "https://img1.wsimg.com/isteam/ip/e24fa177-0274-4498-95db-f1646a64a959/ols/logo%20women's%20t%20back.jpg": merchandiseLogoWomensT,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
